import Role from "byzantine/src/Role";
import {
  ContextForm,
  useLoadingContext,
  useNotificationContext,
} from "cerulean";
import { useLocalization } from "@fluent/react";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Account from "byzantine/src/Account";
import {
  useRolesContext,
  useRoleDetail,
} from "byzantine/src/Role/RolesProvider";
import { useOrganizationUserContext } from "byzantine/src/OrganizationUser/OrganizationUserProvider";
import BaseBanner from "../BaseBanner";
import DiscardRoleChangesDialog from "./DiscardRoleChangesDialog";
import SaveRoleDialog from "./SaveRoleDialog";
import { getActiveUsersForRole } from "./OrganizationRoles.helpers";
import RoleFormBody from "./RoleFormBody";
import useDuplicateRoleNotification from "./useDuplicateRoleNotification";
import useRoleFormData from "./useRoleFormData";
import {
  getDefaultLimits,
  NON_CUSTOM_ROLE_NAMES,
  buildInitialFormData,
  buildRoleFromFormData,
  buildAccountPermissionsPayload,
  buildAccountsOpenedLaterPayload,
  withoutAccountScopedPermissions,
  toRoleFormErrors,
  isRoleFormDirty,
} from "./RoleFormUtils";

interface OrganizationEditRoleProps {
  accounts?: Account[];
  externalAccounts?: Account[];
  orgId: string;
  roleUuid?: string;
  features?: Record<string, any>;
  isUsingConsumerBillpayJson?: string;
  [key: string]: any;
}

interface OrganizationEditRoleFormProps extends OrganizationEditRoleProps {
  /** The role with its per-account permissions, from the role detail endpoint. */
  roleToEdit: Role;
}

function OrganizationEditRoleForm({
  accounts = [],
  externalAccounts = [],
  roleToEdit,
  ...props
}: OrganizationEditRoleFormProps) {
  const { updateRole } = useRolesContext();
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const isUsingConsumerBillpay = !!JSON.parse(
    props.isUsingConsumerBillpayJson || "false"
  );
  const { setIsLoading } = useLoadingContext();
  const { organizationUsers } = useOrganizationUserContext();
  const assignedUsers = getActiveUsersForRole(
    roleToEdit.uuid,
    organizationUsers
  );
  const [isSaving, setIsSaving] = useState(false);
  const duplicateRoleNotification = useDuplicateRoleNotification();
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  // The form's callback for the save waiting on the dialog.
  const pendingSubmit = useRef<((errors?: unknown) => void) | null>(null);

  const BaseBannerComponent = BaseBanner as any;

  const initialData = buildInitialFormData({
    role: roleToEdit,
    accounts,
    externalAccounts,
    defaultLimits: getDefaultLimits(props.features),
    features: props.features,
  });

  const { formData, onChange, onAccountPermissionsChange } =
    useRoleFormData(initialData);
  // A copy, since `ContextForm` edits the form data object in place. The copy is shallow: edits to
  // nested values (accounts, account permissions) must replace them, not change them in place.
  const [savedFormData] = useState(() => ({ ...formData }));

  const onSubmit = async (callback: (errors?: unknown) => void) => {
    duplicateRoleNotification.clear();
    try {
      setIsSaving(true);
      const { role: generatedRole } = buildRoleFromFormData(
        formData,
        roleToEdit.limits
      );

      // Money movement and manage templates permissions are saved per account in
      // `account_permissions`, so they are dropped from the flat `permissions` list.
      const updatedRole = new Role({
        uuid: roleToEdit.uuid,
        name: formData.role_name,
        description: formData.role_description,
        permissions: withoutAccountScopedPermissions(generatedRole.permissions),
        limits: generatedRole.limits,
        organization_uuid: props.orgId,
      });

      await updateRole({
        ...updatedRole.serialize(),
        uuid: roleToEdit.uuid as string,
        account_permissions: buildAccountPermissionsPayload(formData),
        accounts_opened_later: buildAccountsOpenedLaterPayload(formData),
      });
      sendNotification({
        type: "success",
        text: l10n.getString("org-role-update-success"),
      });
      // Wait for toast to render before redirecting
      setTimeout(() => {
        callback();
        navigate(`/roles/${roleToEdit.uuid}`);
      }, 1000);
    } catch (error: unknown) {
      setIsSaving(false);
      if (duplicateRoleNotification.showIfDuplicate(error)) {
        callback();
        return;
      }
      callback(
        toRoleFormErrors(error, {
          nameExists: l10n.getString("org-role-error-name-exists"),
          fallback: l10n.getString("org-role-update-error"),
        })
      );
    }
  };

  // Saving changes access for the role's assigned team members right away, so confirm first.
  const onSubmitRequest = (callback: (errors?: unknown) => void) => {
    if (assignedUsers.length === 0) {
      onSubmit(callback);
      return;
    }
    pendingSubmit.current = callback;
    // The form shows its page-wide loading state until its callback runs, which would cover the
    // dialog. Release it now and show it again when the save actually starts.
    callback();
    setIsSaveDialogOpen(true);
  };

  const onConfirmSave = () => {
    const callback = pendingSubmit.current;
    pendingSubmit.current = null;
    setIsSaveDialogOpen(false);
    if (!callback) return;
    setIsLoading(true);
    onSubmit(callback);
  };

  const onCloseSaveDialog = () => {
    pendingSubmit.current = null;
    setIsSaveDialogOpen(false);
  };

  // Shared by the banner's back button and Cancel: ask first if there are unsaved changes.
  const onLeave = () => {
    if (isSaving) return;
    if (isRoleFormDirty(savedFormData, formData)) {
      setIsDiscardDialogOpen(true);
      return;
    }
    navigate("/roles");
  };

  return (
    <div style={{ minHeight: "800px" }}>
      <BaseBannerComponent
        bannerName={l10n.getString("org-role-edit-title")}
        goBack={onLeave}
      />
      <p>{l10n.getString("org-role-edit-description")}</p>
      <ContextForm data={formData} onChange={onChange}>
        <RoleFormBody
          formData={formData}
          onChange={onChange}
          onAccountPermissionsChange={onAccountPermissionsChange}
          onSubmit={onSubmitRequest}
          onCancel={onLeave}
          isSaving={isSaving}
          features={props.features}
          isUsingConsumerBillpay={isUsingConsumerBillpay}
          accounts={accounts}
          externalAccounts={externalAccounts}
          mode="edit"
        />
      </ContextForm>
      <SaveRoleDialog
        isOpen={isSaveDialogOpen}
        roleName={roleToEdit.name}
        assignedUsers={assignedUsers}
        onConfirm={onConfirmSave}
        onClose={onCloseSaveDialog}
      />
      <DiscardRoleChangesDialog
        isOpen={isDiscardDialogOpen}
        onKeepEditing={() => setIsDiscardDialogOpen(false)}
        onDiscard={() => navigate("/roles")}
      />
    </div>
  );
}

function OrganizationEditRoleContent(props: OrganizationEditRoleProps) {
  const { roles, isLoading, error } = useRolesContext();
  // The save dialog counts the role's active users, so wait for them like the role itself.
  const { isLoading: isUsersLoading, error: usersError } =
    useOrganizationUserContext();
  const { roleUuid: routeRoleUuid } = useParams<{ roleUuid?: string }>();
  const roleUuid = routeRoleUuid || props.roleUuid;
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  const navigate = useNavigate();

  const roleInList = roles.find((role: Role) => role.uuid === roleUuid);
  const isEditable = Boolean(
    roleInList && !NON_CUSTOM_ROLE_NAMES.has(roleInList.name)
  );
  // The roles list is lean; the per-account permissions come from the role detail.
  const {
    data: roleDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useRoleDetail(isEditable ? roleUuid : null);

  useEffect(() => {
    if (!roleUuid) {
      navigate("/roles", { replace: true });
      return;
    }

    if (isLoading || error) {
      return;
    }

    if (!roleInList) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-error-toast"),
      });
      navigate("/roles", { replace: true });
      return;
    }

    if (!isEditable) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-error-toast"),
      });
      navigate(`/roles/${roleInList.uuid}`, { replace: true });
    }
  }, [
    error,
    isLoading,
    isEditable,
    l10n,
    navigate,
    roleInList,
    roleUuid,
    sendNotification,
  ]);

  if (isLoading || isUsersLoading || (isEditable && isDetailLoading)) {
    return <div>{l10n.getString("org-role-loading")}</div>;
  }

  // Without the users the assigned count is unknown, which must not be treated as none.
  const loadError = error || detailError || usersError;
  if (loadError) {
    return (
      <div>{`${l10n.getString("org-role-error-toast")}: ${
        (loadError as Error).message
      }`}</div>
    );
  }

  if (!isEditable || !roleDetail) {
    return null;
  }

  // Keyed by role so a different role always starts from its own saved state.
  return (
    <OrganizationEditRoleForm
      key={roleDetail.uuid}
      {...props}
      roleToEdit={roleDetail}
    />
  );
}

export default function OrganizationEditRole(
  props: OrganizationEditRoleProps
): any {
  return <OrganizationEditRoleContent {...props} />;
}
